/**
 * @module ServiceTestsDynamicColumns
 * @description Builds dynamic Service Test list columns from testBuilder.list configs.
 * Single entry point replaces the former 3-step merge→convert→process pipeline.
 */

import { t } from '@lingui/macro';
import { get, isEmpty } from 'lodash';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { parseAgents, TestAgentsCell } from './formatters/TestAgentsFormatter';
import { getTestConfigTitle } from './ServiceTestsStaticColumns';
import { AGENT_FIELD_NAME } from './ServiceTestsConstants';

const WIDTH_REGEX = /generic-table-fixed-(\d+)/;

/**
 * Parse width from CSS class string.
 * @example parseWidth("generic-table-fixed-150") => 150
 */
function parseWidth(classString) {
    if (!classString) return undefined;
    const match = classString.match(WIDTH_REGEX);
    return match ? Number.parseInt(match[1], 10) : undefined;
}

/**
 * Generate human-readable title from mapping path.
 * @example getTitleFromMapping("testConfig.target") => "Target"
 */
function getTitleFromMapping(mapping) {
    const titleName = mapping.split('.').pop();
    return titleName.charAt(0).toUpperCase() + titleName.slice(1);
}

/**
 * Merge testBuilder.list configs from all test types in a group.
 * portal/ui (testsList.helper.js#addDynamicFields) skips VoipPhone to avoid duplicate
 * columns — both VoIP types share the same 3 column names but with different convert
 * and fieldsAs configs. Instead of hardcoding that skip, we merge by column name:
 *   - columns: union of convert arrays so all formatting converters are preserved
 *   - fieldsAs: collected from ALL testTypes so the per-row mapper can resolve data
 *     from whichever path the row's own testType stores it at
 *
 * Returns merged columns, a set of aliased field names, and a per-type fieldsAs lookup.
 */
function mergeTestTypeConfigs(testTypeArray) {
    const columnsByName = {};
    const aliasedFields = new Set();
    const fieldsAsByType = {};

    (testTypeArray || []).forEach((testType) => {
        const list = testType?.testBuilder?.list;
        if (!list) return;

        (list.columns || []).forEach((col) => {
            if (!columnsByName[col.name]) {
                columnsByName[col.name] = { ...col };
            } else if (col.convert && col.convert.length > 0) {
                const existing = columnsByName[col.name];
                existing.convert = [...new Set([...(existing.convert || []), ...col.convert])];
            }
        });

        (list.fieldsAs || []).forEach((fa) => {
            aliasedFields.add(fa.toField);
        });

        if (list.fieldsAs) {
            const typeMap = {};
            list.fieldsAs.forEach((fa) => {
                typeMap[fa.toField] = fa.fromField;
            });
            fieldsAsByType[testType.name] = typeMap;
        }
    });

    return {
        mergedColumns: Object.values(columnsByName),
        aliasedFields,
        fieldsAsByType,
    };
}

/**
 * Convert a single testBuilder column to a DataTable column definition,
 * applying fieldsAs resolution, renderers, and custom titles in one pass.
 */
function buildColumn(col, aliasedFields, fieldsAsByType) {
    const width = parseWidth(col.class);
    const hasFieldsAs = aliasedFields.has(col.name);
    const hasAgentConvert = col.convert?.includes('TestAgentsParser');

    // Standard agents column (not remapped via fieldsAs)
    if (col.name === AGENT_FIELD_NAME && !hasFieldsAs) {
        return {
            type: COLUMN_TYPES.TEXT,
            mapping: col.name,
            title: t`nPoints`,
            sortable: false,
            tooltip: null,
            width,
            csvFormatter: (value) => {
                if (isEmpty(value)) {
                    return '--';
                }
                return parseAgents(value).agentText;
            },
            renderer: (row, column) => {
                const value = column.mapper(row, column);
                return <TestAgentsCell agents={value} />;
            },
        };
    }

    const column = {
        type: COLUMN_TYPES.TEXT,
        mapping: col.name,
        title: getTitleFromMapping(col.name),
        sortable: true,
        width,
    };

    // Per-row fieldsAs resolution — exactly matches portal/ui's prepareTest()
    // which overwrites toField with fromField data based on each test's OWN
    // testType config. e.g., VoipPhone caller reads from 'agents',
    // VoipPulse caller reads from 'testConfig.caller' directly.
    if (hasFieldsAs) {
        column.sortable = false;
        column.mapper = (row, colDef) => {
            const rowTypeName = row.testTypeObject?.name;
            const sourcePath = fieldsAsByType[rowTypeName]?.[colDef.mapping];
            return sourcePath ? get(row, sourcePath) : get(row, colDef.mapping);
        };
    }

    // Columns with TestAgentsParser convert (VoIP Caller/Callee).
    // Renderer is data-aware: agent objects → TestAgentsCell, primitives → text.
    if (hasAgentConvert) {
        column.sortable = false;
        column.csvFormatter = (value) => {
            if (typeof value === 'string' || typeof value === 'number') {
                return String(value);
            }
            if (isEmpty(value)) {
                return '--';
            }
            return parseAgents(value, 1).agentText;
        };
        column.renderer = (row, colDef) => {
            const value = colDef.mapper(row, colDef);
            if (typeof value === 'string' || typeof value === 'number') {
                return <span>{value}</span>;
            }
            return <TestAgentsCell agents={value} threshold={1} />;
        };
    }

    // Custom title for testConfig fields (uses original mapping, not fieldsAs source)
    const customTitle = getTestConfigTitle(column.mapping);
    if (customTitle) {
        column.title = customTitle;
    }

    return column;
}

/**
 * Build dynamic columns from testBuilder.list configs in one pass.
 * Replaces the former multi-step merge/convert/process pipeline.
 *
 * @param {Array} testTypeArrayObject - Array of test type config objects
 * @returns {Array} Column definitions for DataTable
 */
export function buildDynamicColumns(testTypeArrayObject) {
    const { mergedColumns, aliasedFields, fieldsAsByType } = mergeTestTypeConfigs(testTypeArrayObject);

    if (mergedColumns.length === 0) return [];

    return mergedColumns.map((col) => buildColumn(col, aliasedFields, fieldsAsByType));
}
